import { memo } from 'react';
import clsx from 'clsx';
import { ComponentMemo } from '@headless-workspace/typings';
import {
    PropsWithAriaControls,
    PropsWithAriaExpanded,
    PropsWithAriaLabel,
    PropsWithClassName,
    PropsWithCTA,
    PropsWithCTAContent,
    PropsWithId,
    PropsWithStructuredData,
    PropsWithStyle,
    PropsWithSuppressHydrationWarning,
    PropsWithTestId,
} from '../../props';
import { GlowCTA } from './GlowCTA';
import { GlowCTAContent } from './GlowCTAContent';

type GlowClickableProps = PropsWithAriaLabel &
    PropsWithClassName &
    PropsWithStyle &
    PropsWithId &
    PropsWithTestId &
    PropsWithSuppressHydrationWarning &
    PropsWithAriaControls &
    PropsWithAriaExpanded &
    PropsWithStructuredData &
    PropsWithCTA & {
        content: PropsWithCTAContent;
    };

/**
 * Clickable component used to make call-to-actions
 * Use this component when you need a CTA with an icon and/or text
 * @param ariaLabel - Aria label for the button or anchor
 * @param className - Additional styles
 * @param style - Inline styles
 * @param id - ID of the button or the anchor
 * @param dataTestId - data-testid for the button or anchor
 * @param suppressHydrationWarning - If true, suppress the hydration warning
 * @param content - Content of the clickable text (icon, label, ...)
 * @param ariaControls - ID of the element controlled by the button
 * @param ariaExpanded - If the element controlled by the button is expanded
 * @param structuredData - Optional structured data for SEO. Can contain the following:
 * @param structuredData.itemScope - Indicates that defines the scope of associated metadata
 * @param structuredData.itemProp - Used to add properties to an item
 * @param structuredData.itemType - Specifies the URL of the vocabulary that will be used to define itemprop's (item properties) in the data structure
 * @param props - Action to be performed when the text is clicked (href, onClick, ...)
 */
export const GlowClickable: ComponentMemo<GlowClickableProps> = memo(
    ({
        ariaLabel,
        className,
        style,
        id,
        dataTestId,
        suppressHydrationWarning = false,
        content,
        ariaControls,
        ariaExpanded,
        structuredData,
        ...props
    }) => {
        return (
            <GlowCTA
                ariaLabel={ariaLabel}
                className={clsx('flex flex-row items-center', className)}
                style={style}
                id={id}
                dataTestId={dataTestId}
                suppressHydrationWarning={suppressHydrationWarning}
                ariaControls={ariaControls}
                ariaExpanded={ariaExpanded}
                structuredData={structuredData}
                {...props}
            >
                <GlowCTAContent {...content} />
            </GlowCTA>
        );
    },
);

GlowClickable.displayName = 'GlowClickable';
